import { createContext, useContext } from "react";
import { fmtTime, serviceTypeLabel } from "./utils";

export const DEFAULT_ADDRESS = 'Av. Julius Nyerere, Hulene "B", Maputo';

/** Marca do site da igreja original (textos históricos do site). */
export const LEGACY_BRAND = {
  isDefault: true,
  shortName: 'IEAD Hulene "B"',
  address: DEFAULT_ADDRESS,
  place: 'Templo · Hulene "B"',
};

export function genericBrand(shortName = "") {
  return { isDefault: false, shortName, address: "", place: "Templo", logoUrl: "", primaryColor: "" };
}

/** Logótipo da igreja, ou as iniciais sobre a cor da igreja. */
export function BrandMark({ brand, className = "" }) {
  if (brand.logoUrl) {
    return (
      <img
        src={brand.logoUrl}
        alt={brand.shortName ? `Logótipo ${brand.shortName}` : "Logótipo"}
        className={`${className} object-contain bg-white`}
      />
    );
  }
  return (
    <div
      className={`${className} flex items-center justify-center font-extrabold text-white ${brand.primaryColor ? "" : "bg-gradient-to-br from-indigo-600 to-sky-600"}`}
      style={brand.primaryColor ? { backgroundColor: brand.primaryColor } : undefined}
      aria-hidden="true"
    >
      {brandInitials(brand)}
    </div>
  );
}

export const SiteBrandContext = createContext(LEGACY_BRAND);

export const useSiteBrand = () => useContext(SiteBrandContext);

export function shortPlace(raw, brand = LEGACY_BRAND) {
  const p = String(raw || "").trim();
  if (!p) return brand.place;
  if (p.length > 48 || /igreja evangélica|assembleia de deus/i.test(p)) {
    return brand.place;
  }
  return p;
}

/** Link Google Calendar para um culto. */
export function googleCalendarUrl(service, placeLabel, brand = LEGACY_BRAND) {
  if (!service?.date) return "";
  const date = String(service.date).replace(/-/g, "");
  const time = fmtTime(service.time).replace(":", "") || "0700";
  const start = `${date}T${time}00`;
  let endH = Number(time.slice(0, 2)) + 2;
  if (endH > 23) endH = 23;
  const end = `${date}T${String(endH).padStart(2, "0")}${time.slice(2) || "00"}00`;
  const title = encodeURIComponent(serviceTypeLabel(service) || `Culto ${brand.shortName}`.trim());
  const details = encodeURIComponent(service.theme || service.notes || brand.shortName);
  const location = encodeURIComponent(placeLabel || shortPlace(service.place, brand) || brand.address);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`;
}

/** Nome curto para header/marca; evita visão do plano anual ou nomes longos. */
export function shortChurchName(raw, brand = LEGACY_BRAND) {
  const n = String(raw || "").trim();
  if (!n) return brand.shortName;
  if (n.length > 42) return brand.shortName || n;
  if (/consolidar|expandir a influência|células|visão/i.test(n)) return brand.shortName || n;
  if (/igreja evangélica assembleia de deus/i.test(n)) return brand.shortName || n;
  return n;
}

/** Iniciais para o logótipo textual (ex.: "IEAD Hulene B" → "HB" na igreja original). */
function brandInitials(brand) {
  if (brand.isDefault) return "HB";
  const words = String(brand.shortName || "").replace(/["“”]/g, "").split(/\s+/).filter(Boolean);
  return (words.slice(-2).map((w) => w[0]).join("") || "IG").toUpperCase();
}

/** Endereço completo; no site da igreja original evita mostrar só «Maputo». */
export function resolveAddress(raw, brand = LEGACY_BRAND) {
  const a = String(raw || "").trim();
  if (!brand.isDefault) return a;
  if (!a || a.length < 12 || /^maputo\.?$/i.test(a)) return DEFAULT_ADDRESS;
  if (/maputo/i.test(a) && !/nyerere|hulene/i.test(a)) return DEFAULT_ADDRESS;
  return a;
}
